export type BrandConnectionSyncDecision =
    | { kind: 'waiting_for_authorization' }
    | { kind: 'waiting_for_refresh' }
    | {
          kind: 'ready';
          connectionKeys: string[];
          googleAdsCustomerKeys: string[];
      };

interface ResolveBrandConnectionSyncInput {
    authorizationCompleted: boolean;
    workspaceConnected: boolean;
    initialConnectionKeys: ReadonlySet<string>;
    currentConnectionKeys: ReadonlySet<string>;
    googleAdsCustomerIds?: readonly string[];
}

/**
 * A Brand connection is complete only after the refreshed workspace data
 * exposes a new connection identity. This keeps Brand assignment from racing
 * the OAuth success callback and prevents the authorization modal from closing
 * before the new account can be shown in the Brand.
 */
export const resolveBrandConnectionSync = ({
    authorizationCompleted,
    workspaceConnected,
    initialConnectionKeys,
    currentConnectionKeys,
    googleAdsCustomerIds = [],
}: ResolveBrandConnectionSyncInput): BrandConnectionSyncDecision => {
    if (!authorizationCompleted) {
        return { kind: 'waiting_for_authorization' };
    }

    const connectionKeys = Array.from(currentConnectionKeys).filter(
        (connectionKey) => !initialConnectionKeys.has(connectionKey),
    );

    if (!workspaceConnected || connectionKeys.length === 0) {
        return { kind: 'waiting_for_refresh' };
    }

    const normalizedCustomerIds = Array.from(
        new Set(googleAdsCustomerIds.map((customerId) => String(customerId || '').trim()).filter(Boolean)),
    );

    return {
        kind: 'ready',
        connectionKeys,
        googleAdsCustomerKeys: connectionKeys.flatMap((connectionKey) =>
            normalizedCustomerIds.map((customerId) => `${connectionKey}:${customerId}`),
        ),
    };
};

export interface GoogleAdsCustomerBrandAssignment {
    customerKey: string;
    brandIds: string[];
}

export const resolveGoogleAdsCustomerBrandAssignments = ({
    connectionKeys,
    customerIds,
    brandIdsByCustomerId,
    fallbackBrandIds = [],
}: {
    connectionKeys: readonly string[];
    customerIds: readonly string[];
    brandIdsByCustomerId: Readonly<Record<string, readonly string[]>>;
    fallbackBrandIds?: readonly string[];
}) => {
    const normalizedCustomerIds = Array.from(
        new Set(customerIds.map((customerId) => String(customerId || '').trim()).filter(Boolean)),
    );

    return connectionKeys.flatMap((connectionKey) => normalizedCustomerIds.map((customerId) => ({
        customerKey: `${connectionKey}:${customerId}`,
        brandIds: Array.from(brandIdsByCustomerId[customerId] ?? fallbackBrandIds),
    })));
};

export const hasGoogleAdsCustomerBusinessGroupAssignment = (
    assignments: readonly GoogleAdsCustomerBrandAssignment[],
) => assignments.some(({ brandIds }) => brandIds.length > 0);
